import { IRoomDetail, IRoomsContent } from '@common-types';

export interface IBedGroup {
  bedType: string;
  count: number;
}

export interface IBedroomLayout {
  name: string;
  beds: IBedGroup[];
}

export interface IBathroomSummary {
  label: string;
  count: number;
}

export interface IPropertyRule {
  /** i18n key for the rule's label. */
  labelId: string;
  allowed: boolean;
}

// Statements the supplier files under amenities that are really property rules.
const RULE_LIKE_AMENITY = /^(pets?|smoking)\b/i;
const PETS_RULE = /^pets?\b/i;
const SMOKING_RULE = /^smoking\b/i;
const RULE_NEGATED = /\b(not|no|non)\b/i;
// Bare bathroom fixtures, listed once per bathroom. Counted in the bathroom
// summary instead, and not something a guest reads as an included extra.
const FIXTURE_AMENITY = /^(toilet|sink|shower|bath|bathtub|tub)$/i;
const SLEEPS = /sleeps\s+(\d+)/i;
// A standalone clock reading, so years and totals inside the prose are ignored.
const CLOCK = /\b(\d{1,2})(?::(\d{2}))?\s*([ap])\.?m\.?/i;
const STANDALONE_HOUR = /\b(\d{1,2})\b/;
const NOON = 12;
const MIDNIGHT_AS_24 = 24;

/**
 * Suppliers repeat every bed type at the tail of the amenity list, leave blank
 * entries in it, and mix in rule statements such as "Pets not allowed". Those
 * are surfaced elsewhere, so strip them rather than printing them twice.
 */
export const cleanVacationRentalAmenities = (
  amenities: string[] | null | undefined,
  bedTypes: string[] | null | undefined,
): string[] => {
  const bedTypeSet = new Set((bedTypes || []).map((bed) => bed.trim().toLowerCase()));
  const seen = new Set<string>();

  return (amenities || [])
    .map((amenity) => (typeof amenity === 'string' ? amenity.trim() : ''))
    .filter((amenity) => !!amenity)
    .filter((amenity) => !bedTypeSet.has(amenity.toLowerCase()))
    .filter((amenity) => !RULE_LIKE_AMENITY.test(amenity))
    .filter((amenity) => !FIXTURE_AMENITY.test(amenity))
    .filter((amenity) => {
      const key = amenity.toLowerCase();

      if (seen.has(key)) {
        return false;
      }

      seen.add(key);
      return true;
    });
};

/**
 * Reads the pets and smoking rules from the statements the supplier files under
 * amenities, e.g. "Pets not allowed". Preferred over `petsAllowed` / `nonSmoking`,
 * because prose states the policy outright while `nonSmoking` reads as the
 * negation of what it is rendered as. Falls back to the flags, matching how the
 * rest of the app renders them, when the amenity list says nothing.
 */
export const getPropertyRules = (
  amenities: string[] | null | undefined,
  room: IRoomsContent | null | undefined,
): IPropertyRule[] => {
  const statements = (amenities || []).filter(
    (amenity): amenity is string => typeof amenity === 'string' && !!amenity.trim(),
  );
  const read = (pattern: RegExp): boolean | null => {
    const statement = statements.find((amenity) => pattern.test(amenity.trim()));

    return statement ? !RULE_NEGATED.test(statement) : null;
  };

  const pets = read(PETS_RULE) ?? (room ? !!room.petsAllowed : null);
  const smoking = read(SMOKING_RULE) ?? (room ? !!room.nonSmoking : null);

  return [
    ...(pets === null ? [] : [{ labelId: 'pets.allowed', allowed: pets }]),
    ...(smoking === null ? [] : [{ labelId: 'smoking.allowed', allowed: smoking }]),
  ];
};

/** Capacity is only ever stated in prose, e.g. "5 Bedrooms, 2 Baths, (Sleeps 10)". */
export const parseSleepsCount = (descriptions: string[] | null | undefined): number | null => {
  for (const description of descriptions || []) {
    const match = typeof description === 'string' ? description.match(SLEEPS) : null;

    if (match) {
      return Number(match[1]);
    }
  }

  return null;
};

/**
 * Turns supplier check-in strings such as "Begin time: 16", "10" or "4 PM" into
 * "4:00 PM". Anything without a recognisable clock reading is passed through, so
 * free-form instructions still reach the guest intact.
 */
export const formatCheckTime = (raw: string | null | undefined): string => {
  if (!raw) {
    return '';
  }

  const clock = raw.match(CLOCK);

  if (clock) {
    const hour = Number(clock[1]);

    if (hour >= 1 && hour <= NOON) {
      return `${hour}:${clock[2] || '00'} ${clock[3].toUpperCase()}M`;
    }
  }

  const match = raw.match(STANDALONE_HOUR);

  if (!match) {
    return raw;
  }

  const hour = Number(match[1]);

  if (hour > MIDNIGHT_AS_24) {
    return raw;
  }

  const suffix = hour >= NOON && hour < MIDNIGHT_AS_24 ? 'PM' : 'AM';
  const display = hour % NOON === 0 ? NOON : hour % NOON;

  return `${display}:00 ${suffix}`;
};

// Suppliers append a Spanish-abbreviated quantity, e.g. "Queen (Cant: 2)".
const QUANTITY = /^(.*?)\s*\(\s*cant\.?\s*:\s*(\d+)\s*\)\s*$/i;
// Fixtures and layout notes the supplier files alongside beds in a bedroom.
const NOT_A_BED = /toilet|sink|shower|bath|tub|bidet|^regular$|^standard$/i;
const SINGLE = 1;

/** Splits "Queen (Cant: 2)" into its label and count; unquantified entries count as one. */
const parseQuantifiedDetail = (raw: string): IBedGroup | null => {
  const entry = typeof raw === 'string' ? raw.trim() : '';

  if (!entry) {
    return null;
  }

  const match = entry.match(QUANTITY);

  return match
    ? { bedType: match[1].trim(), count: Number(match[2]) }
    : { bedType: entry, count: SINGLE };
};

const mergeBedGroups = (groups: IBedGroup[]): IBedGroup[] => {
  const counts = new Map<string, number>();

  groups.forEach(({ bedType, count }) => counts.set(bedType, (counts.get(bedType) || 0) + count));

  return Array.from(counts.entries()).map(([bedType, count]) => ({ bedType, count }));
};

/**
 * Reads which beds are in which bedroom. Entries naming a fixture or a layout note
 * ("Regular") are dropped, so a bedroom listing an en-suite toilet does not report
 * it as somewhere to sleep. Bedrooms left with no beds are still returned, since
 * their existence is itself informative.
 */
export const getBedroomLayout = (
  bedroomDetails: IRoomDetail[] | null | undefined,
): IBedroomLayout[] =>
  (bedroomDetails || [])
    .filter(({ name }) => typeof name === 'string' && !!name.trim())
    .map(({ name, details }) => ({
      name: name.trim(),
      beds: mergeBedGroups(
        (details || [])
          .filter((detail) => typeof detail === 'string' && !NOT_A_BED.test(detail.trim()))
          .map(parseQuantifiedDetail)
          .filter((bed): bed is IBedGroup => !!bed),
      ),
    }));

/**
 * Counts bathrooms by kind. `nbrOfBathrooms` lumps full and half baths into one
 * total, which overstates what a guest can shower in, so the kinds are counted
 * from each entry's own classification instead.
 */
export const summariseBathrooms = (
  bathroomDetails: IRoomDetail[] | null | undefined,
): IBathroomSummary[] => {
  const counts = new Map<string, number>();

  (bathroomDetails || []).forEach(({ name, details }) => {
    // The kind appears in the details as an unquantified classification; the name
    // carries it too but with an index appended, e.g. "Half Bath #3".
    const kind = (details || [])
      .map((detail) => (typeof detail === 'string' ? detail.trim() : ''))
      .find((detail) => !!detail && !QUANTITY.test(detail));
    const label = kind || (typeof name === 'string' ? name.replace(/\s*#\d+\s*$/, '').trim() : '');

    if (label) {
      counts.set(label, (counts.get(label) || 0) + SINGLE);
    }
  });

  return Array.from(counts.entries()).map(([label, count]) => ({ label, count }));
};

export const groupBedTypes = (bedTypes: string[] | null | undefined): IBedGroup[] => {
  const counts = new Map<string, number>();

  (bedTypes || [])
    .map((bed) => (typeof bed === 'string' ? bed.trim() : ''))
    .filter((bed) => !!bed)
    .forEach((bed) => counts.set(bed, (counts.get(bed) || 0) + 1));

  return Array.from(counts.entries()).map(([bedType, count]) => ({ bedType, count }));
};

export const getEntireHomeContent = (
  roomsContent: IRoomsContent[] | null | undefined,
  roomKey: string | null | undefined,
): IRoomsContent | undefined => {
  if (!roomsContent?.length) {
    return undefined;
  }

  return roomsContent.find(({ roomKey: key }) => key === roomKey) || roomsContent[0];
};
